import React from "react";
import { useSnapshot } from "valtio";
import { AvForm, AvGroup } from "availity-reactstrap-validation";
import classnames from "classnames";
import {
    CheckboxIcon,
    CrossIcon,
    StyledLabel,
    StyledTextInput,
} from "@aureskonnect/react-ui";
import Creatable from "react-select/creatable";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { mutate } from "swr";
import { uuid } from "uuidv4";

import { store } from "@components/VerticalLayout/store";
import {
    setActions,
    setIsActionsClicked,
    setIsEdited,
    store as locationStore,
    setKey,
} from "./store";

import {
    CustomCardBody,
    CustomCardFooter,
    CustomCardHeader,
} from "@components/Common/CustomCardStyled";
import { CustomSecondaryColorButton } from "@components/Common/CustomSecondaryColorButton";
import { CustomMainColorButton } from "@components/Common/CustomMainColorButton";
import { CodeQR } from "./CodeQR";
import { ModalConfirmationCopy } from "./ModalConfirmationCopy";
import {
    copyUrl,
    downloadQRCode,
    enableCheckbox,
    getModeOfSales,
    getSite,
} from "./helpers";
import ErrorToast from "@components/Common/ErrorTost";
import { useSelector } from "react-redux";

type LocationFormType = {
    setIsAddLocationClicked: Function;
    isConsult: boolean;
    setIsConsult: Function;
    shopId: string;
    qrCodeRefs: any;
    editedData: any;
};
export function LocationForm({
    setIsAddLocationClicked,
    isConsult,
    setIsConsult,
    shopId,
    qrCodeRefs,
    editedData,
}: LocationFormType) {
    const { userID, franchiseID, operatorID } = useSnapshot(store);
    const { isActionsClicked, isEdited } = useSnapshot(locationStore);
    const i18nextLng = localStorage.getItem("i18nextLng") || "fr";
    const { t } = useTranslation();
    const uuidUser = localStorage.getItem("uuidUser");
    let schema: any = useSelector(
        (state: any) => state.global.entityDetails.franchise_schema
    );

    const [isOpened, setIsOpened] = React.useState(false);
    const [isCopy, setIsCopy] = React.useState(false);
    const [modeOfSales, setModeOfSales] = React.useState<any>([]);
    const [modeOfSale, setModeOfSale] = React.useState<any>({
        label: "",
        value: "",
    });
    const [message, setMessage] = React.useState<string>("");
    const [designation, setDesignation] = React.useState<string>("");
    const [qrData, setQrData] = React.useState<any>("");

    const [title, setTitle] = React.useState<string>(t("Add a location"));
    const [inputDesignation, setInputDesignation] = React.useState<boolean>(
        false
    );
    const [canValidate, setCanValidate] = React.useState<boolean>(true);

    async function handleValidate() {
        if (designation === "") {
            setInputDesignation(true);
        }
        if (shopId === undefined && designation !== "") {
            ErrorToast(t("Attention ! you must select a store."));
        } else if (designation !== "") {
            if (editedData.Id !== undefined) editLocation();
            else if (canValidate) addLocation();
        }
    }
    async function addLocation() {
        setCanValidate(false);
        toast.dismiss();
        let dataLocation = {
            [t("Designation")]: designation,
            modeOfSale: modeOfSale,
        };
        let url = await getSite(
            t,
            dataLocation,
            setQrData,
            schema,
            franchiseID,
            userID,
            i18nextLng,
            shopId
        );
        const apiUrlAdd = `${process.env.REACT_APP_API_V2_URL}/settings/general/locationManagement`;
        let id = uuid();
        let data = [
            {
                userId: userID,
                operatorId: operatorID,
                uuidUser: uuidUser,
                shopId: shopId,
                franchiseId: franchiseID,
                adding: true,
                data: {
                    [id]: {
                        designation: designation,
                        modeOfSale: modeOfSale,
                        shopId: shopId,
                        id: id,
                        qrData: url,
                    },
                },
            },
        ];
        const requestOptions = {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(data),
        };
        try {
            mutate(
                apiUrlAdd,
                await fetch(apiUrlAdd, requestOptions)
                    .then((response) => response.json())
                    .then((data) => {
                        if (data.error) {
                            throw Error("error");
                        }
                        setKey(uuid());
                        toast.success(
                            `${t(
                                "The QR code has been successfully generated."
                            )!}`,
                            {
                                position: toast.POSITION.TOP_CENTER,
                                theme: "colored",
                                icon: (
                                    <CheckboxIcon
                                        height={25}
                                        width={25}
                                        fill="white"
                                    />
                                ),
                            }
                        );
                        setDesignation("");
                        setModeOfSale(null);
                        setIsEdited(false);
                        setCanValidate(true);
                    })
            );
        } catch (e) {
            setCanValidate(true);
            toast.error(`${t("There's an error")!}`, {
                position: toast.POSITION.TOP_CENTER,
                autoClose: 2000,
                theme: "colored",
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
        }
    }

    async function editLocation() {
        toast.dismiss();
        let dataLocation = {
            [t("Designation")]: designation,
            modeOfSale: modeOfSale,
        };
        let url = await getSite(
            t,
            dataLocation,
            setQrData,
            schema,
            franchiseID,
            userID,
            i18nextLng,
            shopId
        );
        const apiUrlUpdate = `${process.env.REACT_APP_API_V2_URL}/settings/general/locationManagement`;
        let dataConfig = [
            {
                userId: userID,
                operatorId: operatorID,
                shopId: shopId,
                franchiseId: franchiseID,
                uuidUser: uuidUser,
                data: {
                    [editedData.Id]: {
                        id: editedData.Id,
                        designation: designation,
                        modeOfSale: modeOfSale,
                        shopId: shopId,
                        qrData: url,
                    },
                },
            },
        ];
        const requestOptions = {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(dataConfig),
        };
        try {
            mutate(
                apiUrlUpdate,
                await fetch(apiUrlUpdate, requestOptions)
                    .then((response) => response.json())
                    .then((data) => {
                        if (data.error) {
                            throw Error("error");
                        }
                        setKey(uuid());
                        toast.success(
                            `${t(
                                "The QR code has been successfully generated."
                            )!}`,
                            {
                                position: toast.POSITION.TOP_CENTER,
                                theme: "colored",
                                icon: (
                                    <CheckboxIcon
                                        height={25}
                                        width={25}
                                        fill="white"
                                    />
                                ),
                            }
                        );
                        setDesignation("");
                        setModeOfSale({ label: "", value: "" });
                        setIsAddLocationClicked(false);
                        setIsEdited(false);
                    })
            );
        } catch (e) {
            toast.error(`${t("There's an error")!}`, {
                position: toast.POSITION.TOP_CENTER,
                autoClose: 2000,
                theme: "colored",
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
        }
    }

    const setQrRef = (id: string) => (el: HTMLDivElement | null) => {
        qrCodeRefs.current[id] = el;
    };

    React.useEffect(() => {
        if (shopId !== undefined) {
            getModeOfSales(
                userID,
                franchiseID,
                shopId,
                i18nextLng,
                setModeOfSales
            );
            if (editedData.Id !== undefined) {
                setDesignation(editedData[t("Designation")]);
                setModeOfSale(editedData.modeOfSale);
                setTitle(t("Edit a location"));
                setQrData(editedData.qrData);
            } else {
                setDesignation("");
                setModeOfSale(null);
                setTitle(t("Add a location"));
                setQrData("");
            }
        } else {
            setDesignation("");
            setModeOfSale(null);
            setTitle(t("Add a location"));
            setQrData("");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editedData, franchiseID, shopId, t]);

    return (
        <React.Fragment>
            <AvForm className="card">
                <CustomCardHeader
                    className="d-flex align-items-center justify-content-between"
                    style={{ maxHeight: "76px" }}
                >
                    <h5 className="pt-3">{title}</h5>
                    <CrossIcon
                        className="icon-close-notification__clz mdi_icn_FRcloseForm"
                        height={13}
                        width={13}
                        onClick={() => {
                            setIsConsult(false);
                            if (isEdited === true) {
                                setIsActionsClicked(!isActionsClicked);
                                setActions(() => {
                                    setIsEdited(false);
                                    setIsAddLocationClicked(false);
                                    setIsEdited(false);
                                    enableCheckbox();
                                });
                            } else {
                                setIsAddLocationClicked!(false);
                                setIsEdited(false);
                                enableCheckbox();
                            }
                        }}
                    />
                </CustomCardHeader>
                <CustomCardBody
                    className={`pl-3 pr-5 `}
                    style={{
                        maxHeight: "470px",
                        overflowY: " auto",
                    }}
                >
                    <AvGroup className={`${isConsult ? "pe-none__clz" : ""}`}>
                        <StyledLabel
                            htmlFor="path"
                            className="required__clz mt-3 "
                        >
                            {t("Designation")}
                        </StyledLabel>
                        <StyledTextInput
                            name="designation"
                            placeholder={t("Write")}
                            type="text"
                            autocomplete="off"
                            value={designation}
                            onChange={(e: any) => {
                                setDesignation(e.target.value);
                                setIsEdited(true);
                                setInputDesignation(false);
                            }}
                            className={inputDesignation ? "invalid__clz" : ""}
                        />
                        {inputDesignation ? (
                            <div
                                style={{
                                    width: "100%",
                                    marginTop: "0.25rem",
                                    fontSize: "80%",
                                    color: "#f46a6a",
                                }}
                            >
                                {t("Please enter a designation")}
                            </div>
                        ) : null}
                    </AvGroup>
                    <AvGroup className={`${isConsult ? "pe-none__clz" : ""}`}>
                        <StyledLabel htmlFor="text" className="mt-3">
                            {t("Mode of sale")}
                        </StyledLabel>
                        <Creatable
                            isClearable
                            autocomplete="off"
                            name="saleMethod"
                            onChange={(e: any) => {
                                setModeOfSale(
                                    e === null ? { label: "", value: "" } : e
                                );
                                setIsEdited(true);
                            }}
                            value={modeOfSale}
                            options={modeOfSales}
                            placeholder={`${t("Select")}...`}
                            type="text"
                            maxMenuHeight={150}
                            isValidNewOption={() => false}
                            autoFocus={false}
                        />
                    </AvGroup>
                    <AvGroup className={`${isConsult ? "pe-none__clz" : ""}`}>
                        <StyledLabel
                            htmlFor="text"
                            className="required__clz mt-3"
                        >
                            {t("QR code")}
                        </StyledLabel>
                        <CodeQR
                            qrData={editedData.Id !== undefined ? qrData : ""}
                            qrCodeRefs={qrCodeRefs}
                            isModified={
                                editedData.Id !== undefined
                                    ? editedData.modeOfSale.label !==
                                          modeOfSale?.label ||
                                      editedData[t("Designation")] !==
                                          designation
                                    : false
                            }
                            setIsOpened={setIsOpened}
                            ref={setQrRef(editedData.Id)}
                            Id={editedData.Id}
                            setMessage={setMessage}
                            setIsCopy={setIsCopy}
                        />
                    </AvGroup>
                </CustomCardBody>
                <CustomCardFooter className="pt-1 pb-2">
                    <CustomSecondaryColorButton
                        outline
                        rounded
                        className="mr-2 mdi_btn_FRcancel cmn_btn_cancel"
                        variant="light"
                        onClick={() => {
                            if (isEdited === true) {
                                setIsActionsClicked(!isActionsClicked);
                                setActions(() => {
                                    setIsAddLocationClicked(false);
                                    setIsEdited(false);
                                    enableCheckbox();
                                });
                            } else {
                                setIsAddLocationClicked(false);
                                setIsEdited(false);
                                enableCheckbox();
                            }
                            setIsConsult(false);
                        }}
                    >
                        <span> {t("Cancel")}</span>
                    </CustomSecondaryColorButton>
                    <CustomMainColorButton
                        rounded
                        className={`mdi_btn_FRvalidate cmn_btn_validate ${classnames(
                            { "not-allowed-input-vr__clz": isConsult }
                        )}`}
                        variant="primary"
                        onClick={handleValidate}
                    >
                        {t("Generate QR code")}
                    </CustomMainColorButton>
                </CustomCardFooter>
            </AvForm>
            {isOpened ? (
                <ModalConfirmationCopy
                    isModalOpened={isOpened}
                    setIsModalOpened={setIsOpened}
                    validation={() => {
                        isCopy
                            ? copyUrl(qrData, t)
                            : downloadQRCode(editedData.Id, qrCodeRefs, t);
                    }}
                    message={message}
                />
            ) : null}
        </React.Fragment>
    );
}
